import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { useI18n } from '@/lib/i18n';
import { Info, ArrowRight, Server, Layers, Monitor } from 'lucide-react';

export default function Home() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [campos, setCampos] = useState({ capacidadeU: '', larguraMM: '', profundidadeMM: '', alturaMM: '' });
  const [erro, setErro] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const vals = Object.values(campos);
    if (vals.every(v => v.trim() === '')) return;
    if (vals.some(v => v.trim() !== '' && (isNaN(Number(v)) || Number(v) <= 0))) {
      setErro(t('erroValor'));
      return;
    }
    setErro('');
    const p = new URLSearchParams();
    if (campos.capacidadeU.trim()) p.set('u', campos.capacidadeU.trim());
    if (campos.larguraMM.trim()) p.set('l', campos.larguraMM.trim());
    if (campos.profundidadeMM.trim()) p.set('p', campos.profundidadeMM.trim());
    if (campos.alturaMM) p.set('a', campos.alturaMM.trim());
    navigate(`${ROUTE_PATHS.RESULTADO}?${p.toString()}`);
  };

  const gamaData = [
    { nome: t('nomeNCB'), badge: t('badgeNCB'), specs: t('specsNCB'), badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <Server className="w-4 h-4" />, iconBg: 'bg-primary/10 text-primary' },
    { nome: t('nomeProX'), badge: t('badgeProX'), specs: t('specsProX'), badgeClass: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Layers className="w-4 h-4" />, iconBg: 'bg-amber-50 text-amber-600' },
    { nome: t('nomeSlim'), badge: t('badgeCCTV'), specs: t('specsSlim'), badgeClass: 'bg-slate-100 text-slate-600 border-slate-200', icon: <Monitor className="w-4 h-4" />, iconBg: 'bg-slate-100 text-slate-500' },
  ];

  const campos_config = [
    { id: 'capacidadeU', label: t('capacidadeU'), placeholder: 'ex: 42', hint: 'U' },
    { id: 'larguraMM', label: t('largura'), placeholder: 'ex: 600', hint: 'mm' },
    { id: 'profundidadeMM', label: t('profundidade'), placeholder: 'ex: 800', hint: 'mm' },
    { id: 'alturaMM', label: t('altura'), placeholder: 'ex: 2055', hint: 'mm' },
  ] as const;

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 lg:py-14">

      {/* Hero */}
      <div className="mb-10 max-w-2xl">
        <h1 className="text-3xl lg:text-4xl font-bold text-foreground leading-tight mb-3 whitespace-pre-line">
          {t('homeTitle')}
        </h1>
        <p className="text-[15px] text-muted-foreground leading-relaxed">
          {t('homeSub')}
        </p>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white border border-border/70 rounded-2xl shadow-sm p-6 lg:p-8 mb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
            {campos_config.map(({ id, label, placeholder, hint }) => (
              <div key={id}>
                <label htmlFor={id} className="block text-[12px] font-semibold text-foreground/70 uppercase tracking-wide mb-1.5">
                  {label}
                </label>
                <div className="relative">
                  <input
                    id={id}
                    type="text"
                    inputMode="numeric"
                    placeholder={placeholder}
                    value={campos[id as keyof typeof campos]}
                    onChange={e => setCampos(prev => ({ ...prev, [id]: e.target.value }))}
                    className="w-full h-11 rounded-xl border border-border bg-background px-3.5 pr-10 text-sm font-medium text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-muted-foreground/60 select-none">
                    {hint}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {erro && (
            <p className="text-sm text-destructive mb-4 flex items-center gap-1.5">
              <span className="w-4 h-4 shrink-0">⚠</span> {erro}
            </p>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="submit"
              className="flex items-center justify-center gap-2 h-11 px-7 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 active:scale-[0.98] transition-all shadow-sm shadow-primary/20 cursor-pointer"
            >
              {t('btnEncontrar')}
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="flex items-start gap-1.5 text-[12px] text-muted-foreground">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {t('hintCampos')}
            </p>
          </div>
        </div>
      </form>

      {/* Gamas */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-4">
          {t('gamasTitle')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {gamaData.map((g) => (
            <div key={g.nome} className="bg-white border border-border/70 rounded-xl p-5 hover:border-border hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-2 mb-3">
                <p className="text-[13px] font-semibold text-foreground">{g.nome}</p>
                <span className={`shrink-0 inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${g.badgeClass}`}>
                  {g.badge}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground font-mono leading-relaxed">{g.specs}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
